import { useState, useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { Eye, X } from 'lucide-react';
import { PeriodSelector, validatePeriodMax12Months, type PeriodValue } from '@/shared/components/PeriodSelector.tsx';
import { DownloadButton } from '@/shared/components/DownloadButton.tsx';
import { ReportTable } from '@/shared/components/ReportTable.tsx';
import { useAvailability } from '../hooks/useReports.ts';
import { TenantCombobox } from '@/shared/components/TenantCombobox.tsx';
import type { AvailabilityParams } from '../api/reports.api.ts';
import type { AvailabilityRow } from '@/shared/types/report.ts';

const col = createColumnHelper<AvailabilityRow>();

// Cor do badge de desconto (0% = ok/verde; até 5% amarelo; acima, vermelho).
function descontoBadgeClass(pct: number): string {
  if (pct === 0) return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
  if (pct <= 5) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
  return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
}

const dashClass = 'text-gray-400 dark:text-gray-500';

const MONTH_NOW = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;

interface TabProps {
  availableMonths?: string[];
}

export function AvailabilityTab({ availableMonths }: TabProps) {
  const [period, setPeriod] = useState<PeriodValue>({ mode: 'month', month: MONTH_NOW });
  const [tenantFilter, setTenantFilter] = useState('');
  const [queryParams, setQueryParams] = useState<AvailabilityParams | null>(null);
  const [periodError, setPeriodError] = useState('');

  const { data, isLoading } = useAvailability(queryParams);

  const sorted = useMemo(() => {
    if (!data) return [];
    return [...data].sort((a, b) => Number(a.uptimePercent) - Number(b.uptimePercent));
  }, [data]);

  function handleVisualize() {
    const err = validatePeriodMax12Months(period);
    if (err) { setPeriodError(err); return; }
    setPeriodError('');

    const params: AvailabilityParams = {};
    if (period.mode === 'month') {
      params.month = period.month;
    } else {
      if (!period.from || !period.to) { setPeriodError('Selecione o intervalo completo'); return; }
      params.from = new Date(period.from).toISOString();
      params.to = new Date(period.to).toISOString();
    }
    if (tenantFilter) {
      params.tenantId = tenantFilter;
    }
    setQueryParams(params);
  }

  const downloadParams: Record<string, string | undefined> = queryParams
    ? { ...queryParams }
    : {};

  const columns = useMemo(
    () => [
      col.accessor('tenantNome', { header: 'Tenant' }),
      col.accessor('uptimePercent', {
        header: 'Uptime %',
        cell: (info) => <span className="font-mono">{Number(info.getValue()).toFixed(2)}%</span>,
      }),
      col.accessor('slaAlvo', {
        header: 'SLA Alvo',
        cell: (info) => {
          const v = info.getValue();
          return v != null ? (
            <span className="font-mono">{Number(v).toFixed(2)}%</span>
          ) : (
            <span className={dashClass}>—</span>
          );
        },
      }),
      col.display({
        id: 'slaAtingido',
        header: 'Atingido',
        cell: ({ row }) => {
          const v = row.original.slaAtingido;
          if (v == null) return <span className={dashClass}>—</span>;
          return (
            <span
              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                v
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                  : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
              }`}
            >
              {v ? 'Sim' : 'Não'}
            </span>
          );
        },
      }),
      col.accessor('minutosIndisponiveis', {
        header: 'Min. Fora do Ar',
        cell: (info) => <span className="font-mono">{info.getValue()}</span>,
      }),
      col.accessor('totalIncidentes', {
        header: 'Incidentes',
        cell: (info) => <span className="font-mono">{info.getValue()}</span>,
      }),
      col.accessor('totalManutencoes', {
        header: 'Manutenções',
        cell: (info) => <span className="font-mono">{info.getValue()}</span>,
      }),
      col.display({
        id: 'desconto',
        header: 'Desconto',
        cell: ({ row }) => {
          const v = row.original.descontoPct;
          if (v == null) return <span className={dashClass}>—</span>;
          return (
            <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${descontoBadgeClass(v)}`}>
              {v}%
            </span>
          );
        },
      }),
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 pb-2">
        <div className="relative pb-4">
          <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Período</label>
          <PeriodSelector value={period} onChange={setPeriod} modes={['month', 'range']} availableMonths={availableMonths} error={periodError} />
        </div>
        <div className="pb-4 min-w-[220px]">
          <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Tenant</label>
          <TenantCombobox
            value={tenantFilter}
            onChange={(id) => setTenantFilter(id)}
          />
        </div>
        <div className="pb-4">
          <button
            onClick={handleVisualize}
            disabled={isLoading}
            className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <Eye size={14} />
            {isLoading ? 'Carregando...' : 'Visualizar'}
          </button>
        </div>
        <div className="pb-4">
          <DownloadButton
            url="/admin/reports/availability/pdf"
            params={downloadParams}
            filename="R001-disponibilidade.pdf"
            label="Exportar PDF"
            disabled={!queryParams}
          />
        </div>
        <div className="pb-4">
          <DownloadButton
            url="/admin/reports/availability/csv"
            params={downloadParams}
            filename="R001-disponibilidade.csv"
            label="Exportar CSV"
            disabled={!queryParams}
          />
        </div>
        {(tenantFilter || queryParams) && (
          <div className="pb-4">
            <button
              onClick={() => {
                setTenantFilter('');
                setPeriod({ mode: 'month', month: MONTH_NOW });
                setQueryParams(null);
                setPeriodError('');
              }}
              className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-800"
            >
              <X size={14} />
              Limpar
            </button>
          </div>
        )}
      </div>

      {queryParams && <ReportTable data={sorted} columns={columns} />}
    </div>
  );
}
